import {memo} from 'react'
import {Timeline} from '@primer/react'
import {
  AlertIcon,
  CommentIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  PersonIcon,
  TagIcon,
  InfoIcon,
  EyeIcon,
  EyeClosedIcon,
  GitCommitIcon,
  CrossReferenceIcon,
  CommentDiscussionIcon,
  DiffIcon,
  CheckIcon,
  CopilotIcon,
  GitBranchIcon,
  LockIcon,
  PencilIcon,
  RepoPushIcon,
  RocketIcon,
  StackIcon,
  TableIcon,
  XIcon,
  type Icon,
} from '@primer/octicons-react'
import type {PullRequestReviewInfo, TimelineItem} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import {TimelineItemType} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import type {IssueEventType, PullRequestReviewState} from '../../../page-data/payloads/timeline'
import styles from './TimelinePanel.module.css'

type BadgeConfig = {icon: Icon; className?: string}

const STATIC_BADGE_CONFIG: Partial<Record<TimelineItemType, BadgeConfig>> = {
  [TimelineItemType.ISSUE_COMMENT]: {icon: CommentIcon},
  [TimelineItemType.PULL_REQUEST_COMMIT]: {icon: GitCommitIcon},
  [TimelineItemType.CROSS_REFERENCE]: {icon: CrossReferenceIcon},
  [TimelineItemType.PULL_REQUEST_REVIEW_THREAD]: {icon: CommentDiscussionIcon},
  [TimelineItemType.PULL_REQUEST_REVISION_MARKER]: {icon: DiffIcon},
}

const ISSUE_EVENT_BADGE_CONFIG: Partial<Record<IssueEventType, BadgeConfig>> = {
  merged: {icon: GitMergeIcon, className: 'color-fg-done'},
  closed: {icon: GitPullRequestClosedIcon, className: 'color-fg-closed'},
  reopened: {icon: GitPullRequestIcon, className: 'color-fg-open'},
  assigned: {icon: PersonIcon},
  unassigned: {icon: PersonIcon},
  labeled: {icon: TagIcon},
  unlabeled: {icon: TagIcon},
  review_requested: {icon: EyeIcon},
  review_request_removed: {icon: EyeClosedIcon, className: 'color-fg-muted'},
  head_ref_force_pushed: {icon: RepoPushIcon},
  base_ref_force_pushed: {icon: RepoPushIcon},
  renamed: {icon: PencilIcon},
  locked: {icon: LockIcon},
  unlocked: {icon: LockIcon},
  review_dismissed: {icon: XIcon},
  convert_to_draft: {icon: GitPullRequestDraftIcon},
  ready_for_review: {icon: GitPullRequestIcon},
  deployed: {icon: RocketIcon},
  base_ref_changed: {icon: GitBranchIcon},
  added_to_project_v2: {icon: TableIcon},
  project_v2_item_status_changed: {icon: TableIcon},
  copilot_work_started: {icon: GitBranchIcon},
  referenced: {icon: CrossReferenceIcon},
  added_to_stack: {icon: StackIcon},
  removed_from_stack: {icon: StackIcon},
}

const REVIEW_STATE_BADGE_CONFIG: Partial<Record<PullRequestReviewState, BadgeConfig>> = {
  approved: {icon: CheckIcon, className: 'color-fg-success'},
  changes_requested: {icon: EyeIcon, className: 'color-fg-danger'},
}

function renderBadge({icon: IconComponent, className}: BadgeConfig) {
  return (
    <Timeline.Badge className={styles.activityBadge}>
      <IconComponent size={14} className={className} />
    </Timeline.Badge>
  )
}

function getPullRequestReviewBadge(info: PullRequestReviewInfo) {
  const {reviewerType, state} = info

  if (reviewerType === 'code_scanning' || reviewerType === 'automated') {
    return {icon: AlertIcon}
  }

  if (reviewerType === 'copilot') {
    return {icon: CopilotIcon}
  }

  return REVIEW_STATE_BADGE_CONFIG[state as PullRequestReviewState] ?? {icon: EyeIcon}
}

export const TimelineItemBadge = memo(function TimelineItemBadgeComponent({item}: {item: TimelineItem}) {
  const info = item.additionalInfo?.info
  if (info?.case === 'issueEvent') {
    return renderBadge(ISSUE_EVENT_BADGE_CONFIG[info.value.eventType] ?? {icon: InfoIcon})
  }
  if (info?.case === 'pullRequestReview') {
    return renderBadge(getPullRequestReviewBadge(info.value))
  }
  return renderBadge(STATIC_BADGE_CONFIG[item.type] ?? {icon: InfoIcon})
})
